import { Controller } from "@hotwired/stimulus";
import { renderGrid } from "@giphy/js-components";
import { GiphyFetch } from "@giphy/js-fetch-api";
import { loadFilePond } from "../models/filepond_loader";
import { debounce } from "../helpers/timing_helpers";

const gif = new GiphyFetch("LWEdndeRhD8jeht11s9pvYl9SYnLqQmv");
const DEFAULT_QUERY = "trending";
const TRENDING_LIMIT = 10;
const SEARCH_LIMIT = 25;
const MAX_SEARCH_LENGTH = 50;
const MESSAGE_ATTACHMENT_LIMIT = 5;
const GRID_GUTTER = 8;
const GRID_RENDER_DELAY = 200;
const INITIAL_RENDER_DELAY = 200;
const CATEGORY_LIMIT = 1;
const CATEGORY_CACHE_KEY = "gif_picker_categories";
const CATEGORY_CACHE_TTL = 24 * 60 * 60 * 1000;
const GIF_CATEGORIES = [
  { name: "Trending", query: "trending" },
  { name: "Reactions", query: "reaction" },
  { name: "Greetings", query: "greetings" },
  { name: "Perfect", query: "perfect" },
  { name: "Welcome", query: "welcome" },
  { name: "Congratulations", query: "congratulations" },
  { name: "Thank you", query: "thank you" },
  { name: "Excited", query: "excited" }
];

export default class GifpickerController extends Controller {
  static targets = [
    "modal",
    "search",
    "results",
    "gifUrl",
    "countdown",
    "categories",
    "backButton",
    "backUpload",
    "selectionPreview"
  ];

  static values = {
    maxLength: { type: Number, default: 10 },
  };

  connect() {
    this.initializeState();
    this.initializeHandlers();

    this.setupFilePond().then(() => {
      globalThis.addEventListener("resize", this.renderGrid);
      this.showCategories();
    });
  }

  initializeState() {
    this.filePond = null;
    this.FilePond = null;
    this.selectedGifs = [];
    this.debounceTimer = null;
    this.grid = null;
    this.index = null;
    this.currentQuery = DEFAULT_QUERY;
    this.currentContainer = null;
    this.searchLimitAlertShown = false;
    this.isUploadingGifs = false;
    this.categoryCache = null;
    this.categoryCacheTimestamp = 0;
  }

  initializeHandlers() {
    this.debouncedSearch = debounce((query, index) => {
      this.handleSearch(query, index);
    }, 1000);
  }

  handleGridRender() {
    if (!this.currentContainer) return;

    this.buildGrid();
  }

  handleSearch(query, index) {
    const container = document.getElementById(`results_${index}`);
    this.backButtonTarget.classList.remove("d-none");

    if (!container) return;

    const normalizedQuery = this.normalizeSearchQuery(query);

    if (normalizedQuery === null || normalizedQuery === this.currentQuery) {
      return;
    }

    this.destroyGrid();
    this.showGifResults();

    this.currentContainer = container;
    this.currentQuery = normalizedQuery;

    this.buildGrid();
  }

  normalizeSearchQuery(query) {
    const trimmedQuery = query.trim();

    if (trimmedQuery.length === 1) {
      return null;
    }

    return trimmedQuery || DEFAULT_QUERY;
  }

  initializeMessageGrid() {

    const index = this.element.dataset.index;

    this.currentContainer = document.getElementById(`results_${index}`);
    this.currentQuery = DEFAULT_QUERY;

    setTimeout(() => {
      this.buildGrid();
    }, INITIAL_RENDER_DELAY);
  }

  hideEditModal(event) {
    if (event.target.dataset?.edit !== "true") {
      return;
    }

    const oldModalElement = document.getElementById("shareRecipeModal");

    if (!oldModalElement) {
      return;
    }

    const oldModal = new bootstrap.Modal(oldModalElement);
    oldModal.hide();
  }

  async showCategories() {
    this.destroyGrid();
    this.categoriesTarget?.classList?.remove("d-none");
    this.resultsTarget.classList.add("d-none");
    if (this.selectedGifs.length > 0) {
      this.backUploadTarget.classList.remove("d-none");
      this.backButtonTarget.classList.add("d-none");
    } else {
      this.backUploadTarget.classList.add("d-none");
    }
    this.searchTarget.value = "";

    this.renderSelectionPreview();
    this.renderCategories();
  }

  showGifResults() {
    this.categoriesTarget.classList.add("d-none");
    this.backUploadTarget.classList.remove("d-none");
    this.resultsTarget.classList.remove("d-none");
  }

  async renderCategories() {
    this.categoriesTarget.innerHTML = this.categoryLoader();

    const categories = await this.getCachedCategories();

    this.categoriesTarget.innerHTML = categories
      .map((category) => this.categoryTemplate(category))
      .join("");
  }

  async getCachedCategories() {
    const cached = this.getCategoryCache();

    if (cached) {
      return cached;
    }

    const categories = await Promise.all(
      GIF_CATEGORIES.map((category) => this.fetchCategoryGif(category)),
    );

    this.saveCategoryCache(categories);

    return categories;
  }

  getCategoryCache() {
    try {
      const cached = localStorage.getItem(CATEGORY_CACHE_KEY);

      if (!cached) {
        return null;
      }

      const { data, timestamp } = JSON.parse(cached);

      if (Date.now() - timestamp >= CATEGORY_CACHE_TTL) {
        localStorage.removeItem(CATEGORY_CACHE_KEY);
        return null;
      }

      return data;
    } catch (error) {
      console.error("Failed to read GIF category cache:", error);
      return null;
    }
  }

  saveCategoryCache(categories) {
    try {
      localStorage.setItem(
        CATEGORY_CACHE_KEY,
        JSON.stringify({
          data: categories,
          timestamp: Date.now(),
        }),
      );
    } catch (error) {
      console.error("Failed to save GIF category cache:", error);
    }
  }

  async fetchCategoryGif(category) {
    try {
      const response =
        category.query === DEFAULT_QUERY
          ? await gif.trending({
            limit: CATEGORY_LIMIT,
          })
          : await gif.search(category.query, {
            limit: CATEGORY_LIMIT,
          });

      return {
        ...category,
        imageUrl: response.data?.[0]?.images?.fixed_width?.url || null,
      };
    } catch (error) {
      console.error(`Failed to load ${category.name} GIF:`, error);

      return {
        ...category,
        imageUrl: null,
      };
    }
  }

  categoryTemplate(category) {
    const { imageUrl } = category;

    if (!imageUrl) {
      return `
      <button
        type="button"
        class="btn gif-category position-relative h-100px p-0 overflow-0 rounded-3 overflow-hidden"
        data-query="${category.query}"
        data-action="gifpicker#selectCategory"
      >
        <span class="position-absolute inset-0 d-flex align-items-center justify-content-center text-white fw-bold z-1">${category.name}</span>
      </button>
    `;
    }

    return `
      <button
        type="button"
        class="btn gif-category position-relative h-100px p-0 overflow-0 rounded-3 overflow-hidden"
        data-query="${category.query}"
        data-action="gifpicker#selectCategory"
      >
        <img
          src="${imageUrl}"
          class="w-100 h-100 object-fit-cover"
          alt=""
          loading="lazy"
        >
        <span class="position-absolute inset-0 d-flex align-items-center justify-content-center text-white fw-bold z-1">${category.name}</span>
      </button>
    `;
  }

  categoryLoader() {
    return Array.from({ length: 8 }, () => `
      <div class="gif-category gif-category-loading d-flex align-items-center justify-content-center">
        <span class="text-gray position-static">Loading...</span>
      </div>
    `).join("");
  }

  selectCategory(event) {
    const query = event.currentTarget.dataset.query;
    this.backUploadTarget.classList.remove("d-none");
    this.backButtonTarget.classList.remove("d-none");
    this.showGifResults();

    this.destroyGrid();

    this.currentQuery = query;
    this.currentContainer = this.resultsTarget;

    this.renderSelectionPreview();
    this.buildGrid();
  }

  async buildGrid() {
    if (!this.currentContainer) {
      return;
    }

    const width = this.currentContainer.clientWidth;
    const currentQuery = this.currentQuery || DEFAULT_QUERY;
    const container = this.currentContainer;

    globalThis.clearTimeout(this.gridRenderTimer);
    container.setAttribute("aria-busy", "true");
    container.classList.add("gif-results-loading");
    container.innerHTML = `
      <div class="gif-grid-skeleton" role="status" aria-label="Loading GIFs"
           style="--gif-skeleton-columns: ${this.getGridColumns(width)}">
        ${Array.from({ length: 8 }, () => '<div class="gif-skeleton-tile" aria-hidden="true"></div>').join("")}
      </div>
      <div data-gif-grid></div>
    `;

    this.gridRenderTimer = setTimeout(() => {
      this.renderGifGrid(width, currentQuery, container);
    }, GRID_RENDER_DELAY);
  }

  renderGifGrid(width, currentQuery, container) {
    const gridContainer = container.querySelector("[data-gif-grid]");
    if (!gridContainer) return;

    const finishLoading = () => {
      if (!container.contains(gridContainer)) return;
      container.querySelector(".gif-grid-skeleton")?.remove();
      container.classList.remove("gif-results-loading");
      container.setAttribute("aria-busy", "false");
    };
    const fetchGifs = this.callGif(currentQuery);

    try {
      this.grid = renderGrid(
        {
          width,
          fetchGifs: async (offset) => {
            try {
              const response = await fetchGifs(offset);

              if (!response.data?.length && offset === 0) {
                this.showEmptyState(container);
              }

              return response;
            } finally {
              finishLoading();
            }
          },
          columns: this.getGridColumns(width),
          gutter: GRID_GUTTER,
          noLink: true,
          onGifClick: (selectedGif, event) => {
            event.preventDefault();
            this.toggleGifSelection(selectedGif, event);
          },
        },
        gridContainer,
      );
      this.restoreSelectedState(container);
    } catch (error) {
      this.handleGridError(error, container);
    }
  }

  showEmptyState(container) {
    container.innerHTML = `
      <div class="text-center py-5 text-white">
        <div class="fs-18">No GIFs found.</div>
      </div>
    `;

    container.setAttribute("aria-busy", "false");
  }

  getGridColumns(width) {
    if (width < 576) {
      return 2;
    }

    if (width < 768) {
      return 3;
    }

    return 4;
  }

  handleGridError(error, container) {
    container.classList.remove("gif-results-loading");
    container.setAttribute("aria-busy", "false");
    console.error("GIF search failed:", error);

    const message =
      error?.message?.replace("@giphy/js-fetch-api: ", "") ||
      "Something went wrong";

    container.innerHTML = `
      <div class="text-center py-5 text-muted">
        ${message}
      </div>
    `;
  }

  callGif(currentQuery) {
    if (currentQuery === DEFAULT_QUERY) {
      return (offset) =>
        gif.trending({
          offset,
          limit: TRENDING_LIMIT,
        });
    }

    return (offset) =>
      gif.search(currentQuery, {
        offset,
        limit: SEARCH_LIMIT,
      });
  }

  destroyGrid() {
    globalThis.clearTimeout(this.gridRenderTimer);
    if (typeof this.grid === "function") {
      this.grid();
      this.grid = null;
      return;
    }

    if (this.grid?.remove) {
      this.grid.remove();
      this.grid = null;
    }
  }

  closeModal(index) {
    const modalElement =
      document.getElementById(`gifPickerModal${index}`) ||
      this.element.closest(".modal");

    if (!modalElement) {
      console.error("Modal target not found!");
      return;
    }

    const modal = bootstrap.Modal.getInstance(modalElement);

    if (!modal) {
      console.error("Bootstrap modal instance not found!");
      return;
    }

    modal.hide();
    this.clearSearch();
    this.resultsTarget.innerHTML = "";
    this.resetSelection();
  }

  searchGifs(event) {
    const index =
      event.target.dataset.index || this.element.dataset.index;

    const value = event.target.value.trim();

    if (this.isSearchTooLong(value)) {
      this.handleSearchLimit(event.target, value);
      return;
    }

    this.debouncedSearch(value, index);
  }

  isSearchTooLong(value) {
    return value.length >= MAX_SEARCH_LENGTH;
  }

  handleSearchLimit(input, value) {
    if (!this.searchLimitAlertShown) {
      this.searchLimitAlertShown = true;
      alert("Search term cannot exceed 50 characters.");
    }

    input.value = value.slice(0, MAX_SEARCH_LENGTH);
  }

  async uploadGif(selectedGif) {
    const messageGif = globalThis.location.pathname.includes("messages");
    const url = selectedGif.images.original.url;
    const index = this.index || this.gifUrlTarget.dataset.index;
    const target = this.getUploadTarget(index);

    if (!target) {
      console.error(`Target element post_medias_${index} not found`);
      await this.showAlert(
        "Error",
        "Could not find media upload area.",
        "error",
      );
      return;
    }

    const filePond = await this.getFilePond(target, messageGif);

    const file = await this.fetchGifFile(url);

    if (!file) {
      return;
    }

    if (messageGif) {
      this.addMessageGif(file);
      return;
    }

    await this.addPostGif(filePond, file, index);
  }

  getUploadTarget(index) {
    const form = document.querySelector("[data-gif-enabled]");
    return form?.querySelector(`#${form.dataset?.filepondId}`);
  }

  async getFilePond(target, messageGif) {
    if (messageGif) {
      return null;
    }


    if (!this.filePond) {
      const FilePond = await loadFilePond();
      $(target).removeClass("d-none");

      this.filePond = FilePond.find(target);

      if (!this.filePond) {
        this.filePond = FilePond.create(target, {
          beforeRemoveFile: (item) => this.confirmFileRemoval(item),
        });
      }
    }

    return this.filePond;
  }

  confirmFileRemoval() {
    return new Promise((resolve, reject) => {
      this.showAlert(
        "Confirmation",
        "Are you sure you want to remove this file?",
        "warning",
      ).then((result) => {
        if (result.isConfirmed) {
          this.isValid();
          resolve();
          return;
        }

        reject(new Error("File removal cancelled"));
      });
    });
  }

  async fetchGifFile(url) {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        await this.showAlert("Error", "Failed to load GIF.", "error");
        return null;
      }

      const blob = await response.blob();

      if (!blob.type.startsWith("image/")) {
        await this.showAlert(
          "Error",
          "The selected GIF could not be loaded.",
          "error",
        );
        return null;
      }

      return this.createGifFile(blob);
    } catch (error) {
      console.error("Error loading GIF:", error);

      await this.showAlert(
        "Error",
        "Failed to load GIF.",
        "error",
      );

      return null;
    }
  }

  createGifFile(blob) {
    const extension = this.getFileExtension(blob.type);

    return new File(
      [blob],
      `gif_${Date.now()}.${extension}`,
      { type: blob.type },
    );
  }

  getFileExtension(type) {
    const extensions = {
      "image/gif": "gif",
      "image/webp": "webp",
      "image/png": "png",
      "image/jpeg": "jpg",
    };

    return extensions[type] || "img";
  }

  addMessageGif(file) {
    const messagePreviewController =
      this.application.getControllerForElementAndIdentifier(
        document.getElementById("new_message"),
        "message-preview",
      );

    if (!messagePreviewController) {
      return;
    }

    const attachment = document.getElementById("message_attachments");

    if (!attachment) {
      return;
    }

    const currentFiles = Array.from(attachment.files);

    if (this.hasReachedAttachmentLimit(currentFiles)) {
      this.hideShareModal();
      this.showAttachmentLimit();
      return;
    }

    const dataTransfer = new DataTransfer();

    for (const currentFile of currentFiles) {
      dataTransfer.items.add(currentFile);
    }

    dataTransfer.items.add(file);
    attachment.files = dataTransfer.files;

    messagePreviewController.selectFile({}, file);
    this.hideShareModal();
  }

  hasReachedAttachmentLimit(files) {
    return files.length >= MESSAGE_ATTACHMENT_LIMIT;
  }

  hideShareModal() {
    const modal = bootstrap.Modal.getInstance(
      $("#shareRecipeModal"),
    );

    if (modal) {
      modal.hide();
    }
  }

  showAttachmentLimit() {
    $(".show_error")
      .html("You can upload max 5 attachments")
      .addClass("text-danger");
  }

  async addPostGif(filePond, file, index) {
    if (!filePond) {
      return;
    }

    try {
      await filePond.addFile(file);

      this.closePostGifModal(index);
    } catch (error) {
      console.error("Error adding file to FilePond:", error);

      await this.showAlert(
        "Error",
        "Failed to add GIF to post.",
        "error",
      );
    }
  }

  closePostGifModal(index) {
    const editPost = document.querySelector(
      `.edit-post-details[data-index="${index}"]`,
    );

    if (!editPost) {
      this.closeModal(index);
    }
  }

  async showAlert(title, message, icon) {
    const googleTranslateController = this.getGoogleTranslateController();
    const translations = await this.getAlertTranslations(
      googleTranslateController,
      title,
      message,
    );

    return Swal.fire({
      customClass: {
        popup:
          "swal-popup alert-sm-bg align-items-center justify-content-center w-100 notranslate",
        htmlContainer: "text-black-paint text-white",
      },
      animation: false,
      title: translations.title,
      text: translations.message,
      icon,
      confirmButtonText: translations.ok,
      showCancelButton: true
    });
  }

  getGoogleTranslateController() {
    const element = document.getElementById("google-translate-block");

    if (!element) {
      return null;
    }

    return this.application.getControllerForElementAndIdentifier(
      element,
      "google-translate",
    );
  }

  async getAlertTranslations(controller, title, message) {
    if (!controller) {
      return {
        title,
        message,
        ok: "OK",
      };
    }

    const [translatedTitle, translatedMessage, translatedOk] =
      await controller.translateInterfaceTexts([
        title,
        message,
        "OK",
      ]);

    return {
      title: translatedTitle,
      message: translatedMessage,
      ok: translatedOk,
    };
  }

  async toggleGifSelection(selectedGif, event) {
    const card = event.target.closest(".giphy-gif");

    if (!card) {
      return;
    }

    const selectedIndex = this.selectedGifs.findIndex(
      (gifItem) => gifItem.id === selectedGif.id,
    );

    if (selectedIndex !== -1) {
      this.deselectGif(selectedIndex, card);
      return;
    }

    if ((this?.filePond?.getFiles()?.length || 0) + this.selectedGifs.length >= (this?.maxLengthValue || 10)) {
      this.showAlert("", `You can only select up to ${this?.maxLengthValue} attachments.`, "");
      return;
    }

    if (await this.canSelectGif()) {
      this.selectGif(selectedGif, card);
    }
  }

  deselectGif(index, card) {
    this.selectedGifs.splice(index, 1);
    card.classList.remove("gif-selected");

    this.renderSelectionPreview();
    this.showCountDown();
  }

  async canSelectGif() {
    if (!this.filePond) {
      await this.setupFilePond();
    }

    const existingFiles = this.filePond?.getFiles()?.length || 0;

    return existingFiles + this.selectedGifs.length <
      (this.maxLengthValue || 10);
  }

  selectGif(selectedGif, card) {
    this.selectedGifs.push(selectedGif);
    card.dataset.gifId = selectedGif.id;
    card.classList.add("gif-selected");
    this.renderSelectionPreview();
    this.showCountDown();
  }

  resetSelection() {
    const selectedCards =
      this.currentContainer?.querySelectorAll(".gif-selected") || [];

    for (const element of selectedCards) {
      element.classList.remove("gif-selected");
    }

    this.currentQuery = DEFAULT_QUERY;
    this.selectedGifs = [];
  }

  async done(event) {
    event.preventDefault();

    if (this.isUploadingGifs) {
      return;
    }

    const button =
      event.currentTarget || event.target.parentElement;

    if (this.selectedGifs.length === 0) {
      await this.showAlert(
        "Select GIF",
        "Please select at least one GIF.",
        "warning",
      );
      return;
    }

    this.setUploadingState(button, true);

    try {
      await this.uploadSelectedGifs();
      this.closeModal(this.index);
    } finally {
      this.setUploadingState(button, false);
    }
  }

  async uploadSelectedGifs() {
    this.isUploadingGifs = true;

    const selectedGifs = [...this.selectedGifs];

    for (const selectedGif of selectedGifs) {
      await this.uploadGif(selectedGif);
    }
  }

  setUploadingState(button, uploading) {
    this.isUploadingGifs = uploading;

    button.disabled = uploading;

    if (uploading) {
      button.setAttribute("aria-disabled", "true");
      button.classList.add("gif-loading");
      const btnTextEle = button.querySelector(".translated-settings-label span");
      btnTextEle.textContent = "Uploading";
      btnTextEle?.nextElementSibling?.classList?.remove("d-none");
      return;
    }

    button.removeAttribute("aria-disabled");
    button.classList.remove("gif-loading");
    button.querySelector("span").textContent = "Upload GIF";
  }

  disconnect() {
    globalThis.removeEventListener("resize", this.renderGrid);
    this.destroyGrid();
  }

  hideAllTooltip() {
    setTimeout(() => {
      for (const tooltip of document.querySelectorAll(".tooltip")) {
        tooltip.remove();
      }
    }, 50);
  }

  clearSearch() {
    this.searchTarget.value = "";
    this.searchTarget.dispatchEvent(new Event("input"));
  }

  async setupFilePond() {
    const target = this.getUploadTarget();

    if (!target) {
      return;
    }

    this.FilePond = await loadFilePond();
    this.filePond = this.FilePond.find(target);
  }

  showCountDown() {
    const selectedCount = this.selectedGifs.length;
    const fileCount = this.filePond?.getFiles()?.length || 0;
    const maxLength = this.maxLengthValue || 10;

    this.countdownTarget.textContent =
      `Selected: ${selectedCount}/${maxLength - fileCount}`;
  }

  renderSelectionPreview() {
    const preview = this.selectionPreviewTarget;

    if (!preview) {
      return;
    }

    if (this.selectedGifs.length === 0) {
      preview.innerHTML = "";
      return;
    }

    preview.innerHTML = this.selectedGifs
      .map((gif, index) => this.selectionPreviewTemplate(gif, index))
      .join("");
  }

  selectionPreviewTemplate(gif, index) {
    const imageUrl =
      gif.images?.fixed_width?.url ||
      gif.images?.downsized?.url ||
      gif.images?.original?.url;

    return `
      <div
        class="gif-selection-preview-item w-75px h-75px position-relative flex-shrink-0 rounded-2 overflow-hidden"
        data-gif-id="${gif.id}"
      >
        <img
          src="${imageUrl}"
          alt=""
          loading="lazy"
          class="gif-selection-preview-image w-100 h-100 object-fit-cover d-block"
        >

        <button
          type="button"
          class="btn p-0 cancel-upload-button cursor-pointer position-absolute top-0 end-0 m-2 text-danger fw-bolder fs-18 lh-1"
          aria-label="Remove GIF"
          data-index="${index}"
          data-action="click->gifpicker#removeSelectedGif"
        >
        <i class="ri-close-circle-line"></i>
        </button>
      </div>
    `;
  }

  removeSelectedGif(event) {
    event.preventDefault();
    event.stopPropagation();

    const index = Number(event.currentTarget.dataset.index);

    if (Number.isNaN(index)) {
      return;
    }

    const gifId = this.selectedGifs[index]?.id;
    const selectedGif = this.resultsTarget?.querySelector(
      `.gif-selected[data-gif-id="${gifId}"]`
    );
    if (selectedGif) {
      selectedGif.classList.remove("gif-selected");
    }

    this.selectedGifs.splice(index, 1);

    this.renderSelectionPreview();
    this.showCountDown();

    // this.removeGifSelectionFromGrid();:
  }

  removeGifSelectionFromGrid() {
    if (!this.currentContainer) {
      return;
    }

    this.destroyGrid();
    this.buildGrid();
  }

  restoreSelectedState(container) {
    if (!container || this.selectedGifs.length === 0) {
      return;
    }

    // const selectedIds = new Set(
    //   this.selectedGifs.map((gif) => gif.id),
    // );:

    // const cards = container.querySelectorAll("a");:

    // cards.forEach((card) => {
    // Giphy's generated card needs to be matched against
    // the GIF represented by the card.
    //
    // If the Giphy component exposes the GIF ID on the card,
    // use that attribute here.
    // });
  }
}
